
import Vue from "vue"
import { mapState, mapGetters, mapActions } from "vuex"
import throttle from "lodash/throttle"
import type { MetaInfo } from "vue-meta"
import type { Route } from "vue-router"
import breakpoints from "@/assets/style/_breakpoints.scss"
import type { Hall, FullSession, Session } from "@/types/api/program"
import type { SessionParticipant } from "@/types/api/person"

const isPopout = (route: Route): boolean => "popout" in route.query

export default Vue.extend({
  layout: ({ route }) => isPopout(route) ? "blank" : "",
  layoutSettings: { noPadding: true },

  middleware: [
    "auth",
    async ({ store, route: { params: { hall } }, error, app, redirect }) => {
      await store.dispatch("live/fetchHalls")
      const { halls } = store.state.live
      const isHallExist = halls.some((h: Hall) => h.alias === hall)
      if (isHallExist) return
      const mainHall = halls[0]?.alias
      if (!mainHall) {
        error({ statusCode: 404 })
        return
      }
      return redirect(app.localePath(`/live/${mainHall}`))
    },
  ],

  scrollToTop: false,

  data: () => ({
    session: null as Nullable<FullSession>,
    participants: [] as SessionParticipant[],
    sessions: [] as Session[],
    isMobile: false,
    isModalOpen: false,
  }),

  async fetch () {
    await this.fetchSession()
    // Зависят от session
    await Promise.all([
      this.fetchParticipants(),
      this.fetchSessions(),
    ])
  },

  head (): MetaInfo {
    const { hallAlias } = this
    const hall = this.halls.find((h: Hall) => h.alias === hallAlias)
    return hall
      ? { title: this.getTitle(this.$fmt.localize(hall.title)) }
      : {}
  },

  computed: {
    ...mapState("live", ["halls"]),
    ...mapGetters("app", ["getTitle"]),
    ...mapGetters("user", ["prefs"]),

    isAsideOpen: {
      get (): boolean { return this.prefs.isLiveAsideOpen },
      set (isLiveAsideOpen: boolean) { this.updatePrefs({ isLiveAsideOpen }) },
    },

    hallAlias (): string {
      return this.$route.params.hall
    },

    isPopout (): boolean {
      return isPopout(this.$route)
    },
  },

  watch: {
    hallAlias () { this.$fetch() },
    isMobile (isMobile) {
      if (!isMobile) this.isModalOpen = false
    },
  },

  mounted () {
    const { onResize } = this
    onResize()
    window.addEventListener("resize", onResize, { passive: true })
  },
  beforeDestroy () {
    window.removeEventListener("resize", this.onResize)
  },

  methods: {
    ...mapActions("user", { updatePrefs: "UPDATE_PREFS" }),

    async fetchSession () {
      const { hallAlias } = this
      let [res, err] = await this.$api.program.getHallLiveSession(hallAlias)
      if (err) return console.error(err.response.data)
      const sessionAlias = res?.alias
      if (!sessionAlias) return
      [res, err] = await this.$api.program.getSession(sessionAlias)
      if (err) return console.error(err.response.data)
      this.session = res || null
    },

    async fetchParticipants () {
      const sessionAlias = this.session?.alias
      if (!sessionAlias) return
      const [res, err] = await this.$api.program.getParticipants({
        params: {
          "filter[session]": sessionAlias,
          "sort[lastName]": "asc",
        },
      })
      if (err) return console.error(err.response.data)
      this.participants = this.$api.helpers.getList(res)
    },

    async fetchSessions () {
      const trackId = this.session?.track?.id
      if (!trackId) return
      const [res, err] = await this.$api.program.getSessions({
        params: {
          "filter[track]": trackId,
        },
      })
      if (err) return console.error(err.response.data)
      this.sessions = this.$api.helpers.getList(res)
    },

    onResize: throttle(function (this: any) {
      this.isMobile = window.innerWidth < breakpoints.md
    }, 100),
  },
})
